import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, BarChart, Bar, XAxis, Tooltip, LineChart, Line, YAxis } from "recharts";
import { Target, Activity, TrendingUp, TrendingDown, AlertTriangle, Mic } from "lucide-react";

const LINE_COLORS = ["#CCFF00", "#3D7BFF", "#FFB340", "#B57BFF"];

export function TrainingProfile({ onAskCoach }) {
  const { data: p } = useQuery({ queryKey: ["training-profile"], queryFn: async () => (await api.get("/api/profile/training")).data });
  if (!p) return null;
  const empty = !p.total_sessions;
  const tip = { contentStyle: { background: "#0F1115", border: "1px solid #1E222A", borderRadius: 8, fontSize: 12 }, labelStyle: { color: "#8A91A6" } };

  return (
    <div className="space-y-5" data-testid="training-profile">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="mono-label text-acid">your training profile</span>
          <h2 className="font-heading font-black text-2xl tracking-tight">How you're built right now</h2>
        </div>
        {onAskCoach && (
          <button onClick={onAskCoach} className="inline-flex items-center gap-2 rounded-full border border-acid/60 text-acid px-4 h-10 text-sm hover:bg-acid hover:text-black transition-colors" data-testid="profile-ask-voice">
            <Mic className="w-4 h-4" /> Talk it through with your coach
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Radar */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-5 relative overflow-hidden rounded-xl border border-[#1E222A] bg-gradient-to-br from-[#10131A] to-[#0B0D11] p-5">
          <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full blur-3xl opacity-15 pointer-events-none" style={{ background: "#CCFF00" }} />
          <p className="relative font-heading font-bold flex items-center gap-2"><Target className="w-4 h-4 text-acid" /> Movement balance</p>
          <div className="relative h-72">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={p.radar} outerRadius="72%">
                <PolarGrid stroke="#1E222A" />
                <PolarAngleAxis dataKey="pattern" tick={{ fill: "#B7BECB", fontSize: 12 }} />
                <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                <Radar dataKey="score" stroke="#CCFF00" strokeWidth={2} fill="#CCFF00" fillOpacity={0.28} isAnimationActive />
              </RadarChart>
            </ResponsiveContainer>
            {empty && <p className="absolute inset-0 flex items-center justify-center text-center text-sm text-[#8A91A6] px-10">Log a few lifts with your coach and your shape appears here.</p>}
          </div>
        </motion.div>

        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
          {/* Consistency ring */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="rounded-xl border border-[#1E222A] bg-[#0F1115] p-5 flex items-center gap-5">
            <Ring pct={p.consistency} />
            <div>
              <p className="font-heading font-bold flex items-center gap-2"><Activity className="w-4 h-4 text-acid" /> Consistency</p>
              <p className="text-sm text-[#8A91A6] mt-1">{p.active_days_28} training days in the last 4 weeks. Three a week scores 100%.</p>
            </div>
          </motion.div>

          {/* Weak points */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="rounded-xl border border-[#FFB340]/30 bg-[#0F1115] p-5">
            <p className="font-heading font-bold flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-[#FFB340]" /> Weak points</p>
            <div className="mt-2 space-y-2">
              {p.weak_points.length === 0 && p.plateaus.length === 0 && <p className="text-sm text-[#8A91A6]">{empty ? "Nothing to flag yet." : "Nicely balanced. Keep it up."}</p>}
              {p.weak_points.map((w) => (
                <div key={w.pattern} className="text-sm"><span className="text-white font-medium">{w.pattern}</span> <span className="text-[#8A91A6]">· {w.tip}</span></div>
              ))}
              {p.plateaus.map((m) => (
                <div key={m} className="text-sm"><span className="text-white font-medium">{m}</span> <span className="text-[#8A91A6]">· no new best in 3 weeks. Try a new rep range.</span></div>
              ))}
            </div>
          </motion.div>

          {/* Weekly volume */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="sm:col-span-2 rounded-xl border border-[#1E222A] bg-[#0F1115] p-5">
            <p className="font-heading font-bold">Weekly volume <span className="text-[#8A91A6] font-normal text-sm">· kg lifted (sets × reps × weight)</span></p>
            <div className="h-36 mt-2">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={p.weeks.map((w) => ({ ...w, label: new Date(w.week_start).toLocaleDateString(undefined, { day: "numeric", month: "short" }) }))}>
                  <XAxis dataKey="label" tick={{ fill: "#565C6F", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip {...tip} cursor={{ fill: "rgba(255,255,255,0.04)" }} formatter={(v) => [`${Number(v).toLocaleString()} kg`, "Volume"]} />
                  <Bar dataKey="volume_kg" fill="#3D7BFF" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Lift trends */}
      {p.trends.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {p.trends.map((t, i) => {
            const up = t.change_pct >= 0;
            return (
              <motion.div key={t.movement} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * i }}
                className="rounded-xl border border-[#1E222A] bg-[#0F1115] p-4">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-sm truncate">{t.movement}</p>
                  <span className={`text-xs inline-flex items-center gap-1 ${up ? "text-acid" : "text-[#FF6B6B]"}`}>
                    {up ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}{up ? "+" : ""}{t.change_pct}%
                  </span>
                </div>
                <p className="font-heading font-black text-2xl mt-1">{t.points[t.points.length - 1].est_1rm}<span className="text-sm text-[#8A91A6] font-medium"> kg est. 1RM</span></p>
                <div className="h-14 mt-1">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={t.points}>
                      <YAxis hide domain={["dataMin - 5", "dataMax + 5"]} />
                      <Tooltip {...tip} formatter={(v) => [`${v} kg`, "est. 1RM"]} />
                      <Line type="monotone" dataKey="est_1rm" stroke={LINE_COLORS[i % 4]} strokeWidth={2.5} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Ring({ pct }) {
  const R = 34, C = 2 * Math.PI * R;
  return (
    <svg width="88" height="88" viewBox="0 0 88 88" className="shrink-0" aria-label={`Consistency ${pct}%`}>
      <circle cx="44" cy="44" r={R} stroke="#1E222A" strokeWidth="8" fill="none" />
      <motion.circle cx="44" cy="44" r={R} stroke="#CCFF00" strokeWidth="8" fill="none" strokeLinecap="round"
        strokeDasharray={C} initial={{ strokeDashoffset: C }} animate={{ strokeDashoffset: C * (1 - pct / 100) }}
        transition={{ duration: 1.2 }} transform="rotate(-90 44 44)" style={{ filter: "drop-shadow(0 0 6px rgba(204,255,0,0.6))" }} />
      <text x="44" y="50" textAnchor="middle" fontWeight="900" fontSize="18" fill="#fff">{pct}%</text>
    </svg>
  );
}
